// src/features/streams/useStreamLiveStatus.ts

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { MEDIA_QUERY_KEYS } from '@/lib/constants/media';
import { LIVE_STATUS_POLL_INTERVAL_MS } from '@/lib/constants/stream';
import { StreamApi } from './StreamApi';
import { StreamUtils } from './StreamUtils';
import type { RealtimeStreamData } from '@/lib/types/Stream';

/**
 * Polls realtime stream statuses every 15 seconds.
 * Returns a Map<mediaId, RealtimeStreamData> for O(1) lookup per row.
 *
 * Key design decisions:
 * - Uses TanStack Query refetchInterval for automatic polling
 * - Polling pauses when window loses focus (built-in behavior)
 * - Map is memoized; only re-creates when data reference changes
 * - Duplicate mediaId entries (multiple outputs): prefer isRecording === true
 */
export function useStreamLiveStatus(options?: { enabled?: boolean }) {
  const { selectedCId } = useAuth();

  const query = useQuery({
    queryKey: MEDIA_QUERY_KEYS.streamLiveStatus(selectedCId ?? 0),
    queryFn: () => StreamApi.getRealtimeStreams(selectedCId!),
    // `enabled: false` stops the 15 s poll on surfaces that show no stream
    // (a category page without live items); the cached map still serves.
    enabled: (options?.enabled ?? true) && selectedCId != null,
    refetchInterval: LIVE_STATUS_POLL_INTERVAL_MS,
    staleTime: LIVE_STATUS_POLL_INTERVAL_MS - 1000,
    gcTime: 5 * 60 * 1000,
  });

  const liveStatusMap = useMemo(() => {
    if (!query.data) return new Map<number, RealtimeStreamData>();
    return StreamUtils.buildLiveStatusMap(query.data.data);
  }, [query.data]);

  return { liveStatusMap, isLoading: query.isLoading };
}
